import { TILE, project, tileUrl } from '../lib/tiles.js'

/**
 * The venue map - build-spec §7b.
 *
 * Plain OSM raster tiles in a grid of `<img>` elements. There used to be a
 * second stage that swapped in MapLibre for pan and zoom; it is gone.
 *
 * Two reasons. MapLibre v6 requires WebGL2, which is missing on plenty of real
 * machines — remote sessions, older GPUs, hardened profiles — and there it
 * renders nothing at all. And the interaction it bought was never wanted here:
 * this map answers "where is that", once, on a card the user is deciding
 * about. Directions are one tap away for everything else.
 *
 * So: no library, no WebGL, no initialisation, ~1MB less JavaScript, and it
 * paints wherever a browser can show a picture.
 *
 * Never Google tiles - spec §8 says the frontend never talks to Google, and an
 * embed would report the venue to them on every card view. Self-hosted
 * Protomaps is the next step when tile volume matters.
 */

const ZOOM = 13

interface Props {
  lat: number
  lng: number
  name: string
}

export function VenueMap({ lat, lng, name }: Props) {
  const { x, y, offsetX, offsetY } = project(lat, lng, ZOOM)
  /**
   * A symmetric ring around the pin's tile.
   *
   * Rows used to be [-1, 0] — one tile above the pin and one below — while the
   * grid is offset a full tile upward to centre it. That leaves only
   * (256 - offsetY) pixels of map beneath the pin, so whenever the venue sat
   * low in its tile the bottom of the frame ran out of tiles and showed the
   * background: the reported grey band, appearing "sometimes" because it
   * depends on where in its tile the venue happens to fall.
   */
  const cols = [-1, 0, 1]
  const rows = [-1, 0, 1]

  return (
    <figure className="venue-map">
      <div
        className="venue-map-grid"
        style={{
          left: `calc(50% - ${offsetX}px - ${TILE}px)`,
          top: `calc(50% - ${offsetY}px - ${TILE}px)`,
          gridTemplateColumns: `repeat(${cols.length}, ${TILE}px)`,
        }}
      >
        {rows.map((dy) =>
          cols.map((dx) => (
            <img
              key={`${x + dx},${y + dy}`}
              src={tileUrl(x + dx, y + dy, ZOOM)}
              width={TILE}
              height={TILE}
              alt=""
              loading="lazy"
              decoding="async"
            />
          )),
        )}
      </div>

      <span className="venue-map-pin" aria-hidden>
        <svg viewBox="0 0 24 32" width="26" height="35" fill="none">
          <path
            d="M12 1c6.1 0 11 4.9 11 11 0 7.7-11 19-11 19S1 19.7 1 12C1 5.9 5.9 1 12 1z"
            fill="var(--accent)"
            stroke="var(--accent-ink)"
            strokeWidth="1.5"
          />
          <circle cx="12" cy="12" r="4" fill="var(--accent-ink)" />
        </svg>
      </span>

      <figcaption className="sr-only">{name}</figcaption>

      {/* Required wherever OSM tiles are shown. */}
      <small className="venue-map-credit">© OpenStreetMap</small>
    </figure>
  )
}
